/**
 * Shared file-import data contract for the list features.
 *
 * `FileImportData` is what a completed upload→map→analyze flow produces: the
 * deferred-import payload (`FileImportPayload`) plus the analyze report and the
 * source file name. The wizards (Add / Edit) store it for import at save time;
 * the standalone file-import feature builds its request from the same shape.
 * Pure and framework-free so every feature can depend on it without depending
 * on each other.
 */
import type { CsvAnalyzeResponse, CsvParseResponse } from '@shared/schemas';

import type { FileImportPayload } from './list-save.model';

/** Data produced by the file-import flow, stored for deferred import at save time. */
export interface FileImportData extends FileImportPayload {
  analyzeResult: CsvAnalyzeResponse;
  fileName: string;
}

// ── Embedded upload flow (Add / Edit wizards) ──

export type FileAnalyzeStatus = 'idle' | 'running' | 'succeeded' | 'failed';

/**
 * State of the embedded upload → map → analyze mini-flow inside the list
 * wizards. The flow's output is a `FileImportData` (kept next to this state by
 * each wizard slice); `analyzeStatus: 'succeeded'` is the one-shot signal the
 * step UI acknowledges before advancing the wizard.
 */
export interface FileUploadState {
  /** 0 = Upload, 1 = Map Columns, 2 = Analyze report. */
  step: 0 | 1 | 2;
  fileName: string | null;
  parsing: boolean;
  parseResult: CsvParseResponse | null;
  columnMapping: Record<string, string>;
  analyzeStatus: FileAnalyzeStatus;
}

export const initialFileUploadState: FileUploadState = {
  step: 0,
  fileName: null,
  parsing: false,
  parseResult: null,
  columnMapping: {},
  analyzeStatus: 'idle',
};

// Pure transitions, delegated to by both wizard slices (same pattern as
// `filter-tree.ts`) so Add and Edit share the flow without sharing a slice.

export function applyParseStarted(state: FileUploadState, fileName: string): FileUploadState {
  return { ...state, parsing: true, fileName };
}

export function applyParseSucceeded(
  state: FileUploadState,
  parseResult: CsvParseResponse,
): FileUploadState {
  return { ...state, parsing: false, parseResult, columnMapping: parseResult.auto_mapping, step: 1 };
}

export function applyParseFailed(state: FileUploadState): FileUploadState {
  return { ...state, parsing: false };
}

export function applyMappingChanged(
  state: FileUploadState,
  header: string,
  field: string,
): FileUploadState {
  return { ...state, columnMapping: { ...state.columnMapping, [header]: field } };
}

export function applyAnalyzeStarted(state: FileUploadState): FileUploadState {
  return { ...state, analyzeStatus: 'running' };
}

export function applyAnalyzeSucceeded(state: FileUploadState): FileUploadState {
  return { ...state, analyzeStatus: 'succeeded', step: 2 };
}

export function applyAnalyzeFailed(state: FileUploadState): FileUploadState {
  return { ...state, analyzeStatus: 'failed' };
}

/** The step UI acknowledges a success before advancing, so a remount can't re-advance. */
export function applyAnalyzeAcknowledged(state: FileUploadState): FileUploadState {
  return { ...state, analyzeStatus: 'idle' };
}

export function applyStepBack(state: FileUploadState): FileUploadState {
  return { ...state, step: Math.max(0, state.step - 1) as FileUploadState['step'] };
}
